<template>
  <v-ons-page>
    <div class="page__background" style="background-color: white" />
    <div class="ja-stream-group-confirm">
      <common-header>
        <template #header>
          <span>配信グループの確認</span>
        </template>
      </common-header>
      <div class="stream-group-confirm-area">
        <div class="stream-group-element-area">
          <label>配信コンテンツ</label>
          <p>{{ streamGroupContentList.length }}コンテンツ</p>
        </div>
        <div v-if="streamGroupContentList.length">
          <draggable
            v-model="streamGroupContentList"
            item-key="contentId"
            handle=".handle"
            :force-fallback="true"
          >
            <template #item="{ element }">
              <ja-content-row
                :content="element"
                :selectedContentList="[]"
                :checkContent="() => {}"
                :setContentDuration="setContentDuration"
                :isEditable="true"
              />
            </template>
          </draggable>
        </div>
        <empty-content v-else :emptyPageNum="EMPTY_PAGE_NUM.CONTENT" />
        <div class="stream-group-element-area">
          <span class="gray-color">ドラッグで順番を変更できます</span>
        </div>
        <div class="stream-group-element-area">
          <label>合計時間</label>
          <div class="duration-wrapper d-flex items-center">
            <img
              :src="require(`@/assets/images/${STREAM_GROUP_DURATION_ICON}`)"
              alt="stream group duration icon"
            />
            <span class="stream-group-duration">{{ convertSecTommss(streamGroupDuration) }}</span>
          </div>
        </div>
        <div class="stream-group-element-area">
          <label>タイトル</label>
          <p>{{ streamGroupName }}</p>
        </div>
      </div>
      <common-footer class="footer">
        <template #footer>
          <v-ons-row class="footer-button-area">
            <small-button
              class="footer-button footer-back-button"
              title="戻る"
              :disabled="false"
              @click="() => moveBackButton(router)"
            />
            <small-button
              class="footer-button footer-next-button"
              title="保存する"
              :disabled="!streamGroupContentList.length"
              @click="streamGroupUpload"
            />
          </v-ons-row>
        </template>
      </common-footer>
    </div>
  </v-ons-page>
</template>
<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import SmallButton from '@/components/common/SmallButton.vue';
import EmptyContent from '@/components/common/EmptyContent.vue';
import JaContentRow from '@/components/JaStreamGroup/JaContentRow.vue';
import { deleteFailureUploadStreamGroups, postStreamGroup } from '@/composable/data_push_common';
import { stopPolling, convertSecTommss } from '@/composable/material_common';
import { moveBackButton, checkNetwork } from '@/composable/common';
import { EMPTY_PAGE_NUM } from '@/composable/constant_common';
import draggable from 'vuedraggable';

const store = useStore();
const router = useRouter();
// 配信グループコンテンツリスト
const streamGroupContentList = ref([]);

// 配信グループ再生秒数アイコンファイル名
const STREAM_GROUP_DURATION_ICON = 'IconStreamGroupDuration.png';
// パラメータ追加用コンテンツIDリスト
const parameterContentIdList = ref([]);
// パラメータ追加用コンテンツ再生秒数リスト
const parameterContentDurationList = ref([]);
// パラメータ追加用コンテンツ順序リスト
const parameterContentOrderList = ref([]);
// 配信グループ再生秒数
const streamGroupDuration = ref(0);
// ボタン連打回避のためフラグ定義
const isAxiosWait = ref(false);

onMounted(() => {
  streamGroupContentList.value = store.getters['streamGroup/getStreamGroupContentList'];
});
/**
 * 配信グループ名取得
 */
const streamGroupName = computed(() => store.getters['streamGroup/getStreamGroupName']);

/**
 * 配信グループコンテンツリスト(画像コンテンツ再生秒数)更新に伴う配信グループ再生秒数計算処理
 */
watch(
  streamGroupContentList,
  (newStreamGroupContentList) => {
    streamGroupDuration.value = newStreamGroupContentList.reduce(
      (sumContentDuration, current) => sumContentDuration + current.contentDuration,
      0
    );
  },
  { deep: true }
);

/**
 * 配信グループアップロード処理
 */

const streamGroupUpload = async () => {
  // ボタン連打回避のためフラグ設定
  isAxiosWait.value = true;
  try {
    // ネットワークチェック
    await checkNetwork();
    // ポーリング解除
    stopPolling();
    // アップロードに失敗した配信グループの削除
    await deleteFailureUploadStreamGroups();

    // パラメータとなるコンテンツリストの生成
    streamGroupContentList.value.forEach((value, index) => {
      parameterContentIdList.value.push(value.contentId);
      parameterContentDurationList.value.push(value.contentDuration);
      parameterContentOrderList.value.push(index);
    });
    // パラメータリストの生成
    const StreamGroupParameters = new FormData();
    StreamGroupParameters.append('userId', store.getters['userInformation/getUserId']);
    StreamGroupParameters.append('streamGroupName', streamGroupName.value);
    StreamGroupParameters.append('isStreaming', store.getters['streamGroup/getIsStreaming']);
    StreamGroupParameters.append('contentCount', streamGroupContentList.value.length);
    StreamGroupParameters.append('streamGroupDuration', streamGroupDuration.value);
    StreamGroupParameters.append('contentIdList', parameterContentIdList.value);
    StreamGroupParameters.append('contentDurationList', parameterContentDurationList.value);
    StreamGroupParameters.append('contentOrderList', parameterContentOrderList.value);

    // 呼び出しAPI判定(PUTの場合は配信グループIDをパラメータに追加)
    let method = 'post';
    if (store.getters['streamGroup/getIsStreamGroupUpdate']) {
      method = 'put';
      StreamGroupParameters.append('streamGroupId', store.getters['streamGroup/getStreamGroupId']);
    }
    // 配信グループ登録
    await postStreamGroup(method, StreamGroupParameters);

    // 配信グループ登録成功時、配信グループ管理画面へ遷移
    router.push('/ja/streamGroup');
  } finally {
    // ボタン連打回避のためフラグ解除
    isAxiosWait.value = false;
  }
};
/**
 * コンテンツの長さを選択された時間で設定する処理
 */
const setContentDuration = (contentId, duration) => {
  const targetIndex = streamGroupContentList.value.findIndex(
    (content) => content.contentId === contentId
  );
  const targetContent = { ...streamGroupContentList.value[targetIndex] };
  targetContent.contentDuration = parseInt(duration, 10);
  streamGroupContentList.value.splice(targetIndex, 1, targetContent);
};
</script>
<style scoped>
.ja-stream-group-confirm {
  margin: auto;
}
.gray-color {
  color: #aaaaaa;
  font-size: 1.5rem;
}
.stream-group-confirm-area {
  box-sizing: border-box;
  background-color: #fff;
  height: 100%;
  margin: auto;
  padding-top: 10vh;
  padding-top: 10dvh;
  padding-bottom: 15vh;
  padding-bottom: 15dvh;
  z-index: 1;
  max-width: 840px;
}
.stream-group-element-area {
  margin: auto auto 2rem;
  width: 90%;
}
label {
  display: block;
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
}
p {
  font-size: 1.5rem;
  margin: 0;
}
.duration-wrapper {
  height: 100%;
  width: 100%;
}
.stream-group-duration {
  padding: 0rem 0rem 0rem 1rem;
  font-size: 1.5rem;
}

.footer-button-area {
  height: 100%;
  width: 90%;
}
.footer-button {
  margin: auto;
  padding: 1rem 0;
  text-align: center;
}
.footer-back-button {
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  width: 25%;
}
.footer-next-button {
  box-shadow: 0rem 0.2rem #21963b;
  color: #fff;
  width: 65%;
  cursor: pointer;
}
.footer-next-button:hover {
  opacity: 0.8;
}
.footer {
  height: 9rem;
  flex-direction: column;
}
</style>
